// Contact — page publique (indexable).
// Envoi via API backend (Resend) — jamais de client mail natif.

import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEOHead from "../SEOHead";
import { seoConfig, jsonLdBreadcrumb } from "../seoConfig";

const P = seoConfig.pages.contact;
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const Contact = () => {
  const [params] = useSearchParams();
  const isDemoRequest = params.get("type") === "demo";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isDemoRequest && !message) {
      setMessage("Bonjour, je souhaite une démonstration de WYVIO pour mon agence. Merci de me recontacter.");
    }
  }, [isDemoRequest, message]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          company: company || null,
          message,
          type: isDemoRequest ? "demo" : "contact",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail || "Impossible d'envoyer votre message pour le moment.";
        toast.error(detail);
        return;
      }
      toast.success(isDemoRequest ? "Demande de démo envoyée." : "Votre message a bien été envoyé.");
      setSent(true);
      setName(""); setEmail(""); setCompany(""); setMessage("");
    } catch (err) {
      toast.error("Impossible d'envoyer votre message pour le moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title={P.title}
        description={P.description}
        keywords={P.keywords}
        path={P.path}
        jsonLd={jsonLdBreadcrumb([
          { name: "Accueil", path: "/" },
          { name: "Contact", path: P.path },
        ])}
      />

      <section className="mx-auto max-w-6xl px-6 lg:px-10 pt-20 pb-16 lg:pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-14">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
              Contact
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#111]">
              Parlez à un membre<br />de l&apos;équipe.
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-[#6B7280] max-w-md">
              Une question sur la plateforme, un déploiement pour votre équipe, une
              demande de démonstration : écrivez-nous, nous répondons rapidement.
            </p>

            <div className="mt-10 space-y-5">
              <button
                type="button"
                onClick={() => document.querySelector('[data-testid="contact-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
                data-testid="contact-email"
                className="flex items-center gap-4 group text-left"
              >
                <div className="h-11 w-11 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111] group-hover:bg-[#111] group-hover:text-white transition">
                  <Mail size={17} strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[12px] uppercase tracking-widest text-[#6B7280]">Email</p>
                  <p className="text-[15px] font-medium text-[#111]">hello@wyvio.app</p>
                </div>
              </button>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111]">
                  <Phone size={17} strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[12px] uppercase tracking-widest text-[#6B7280]">Téléphone</p>
                  <p className="text-[15px] font-medium text-[#111]">Sur rendez-vous</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-black/5 bg-[#F7F8FA] p-8 lg:p-10"
          >
            {sent ? (
              <div data-testid="contact-form-sent" className="text-center py-8">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#C8FF2F] flex items-center justify-center">
                  <CheckCircle2 size={22} className="text-[#111]" />
                </div>
                <h2 className="mt-5 text-xl font-semibold text-[#111]">
                  Votre message a bien été envoyé.
                </h2>
                <p className="mt-2 text-[14px] text-[#6B7280] leading-relaxed max-w-sm mx-auto">
                  Nous vous répondrons sous 24h ouvrées à l&apos;adresse que vous avez indiquée.
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-[#111]">Écrivez-nous</h2>
                <p className="mt-1.5 text-[13px] text-[#6B7280]">
                  Nous vous répondons sous 24h ouvrées.
                </p>

                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-[12px] font-medium text-[#111]">Votre nom</span>
                    <input
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="contact-name"
                      className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] outline-none focus:border-[#111]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-medium text-[#111]">Entreprise</span>
                    <input
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      data-testid="contact-company"
                      className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] outline-none focus:border-[#111]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-medium text-[#111]">Email professionnel</span>
                    <input
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="contact-mail"
                      className="mt-1.5 w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] outline-none focus:border-[#111]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-medium text-[#111]">Votre message</span>
                    <textarea
                      required
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid="contact-message"
                      className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[14px] outline-none focus:border-[#111]"
                    />
                  </label>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="mt-6 h-12 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium hover:bg-black transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Envoi en cours…" : "Envoyer mon message"}
                  {!submitting && <ArrowRight size={16} strokeWidth={2.25} />}
                </button>
                <p className="mt-3 text-[11.5px] text-[#6B7280] text-center">
                  Votre message arrivera directement à l&apos;équipe WYVIO. Nous répondons sous 24h ouvrées.
                </p>
              </>
            )}
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
